import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AppHeaderBar from "./AppHeaderBar";
import AppNavBar from "./AppNavBar";
import { fetchNotification } from "../redux/notificationSlice";
import { countUnreadAllowedNotifications } from "./notificationUtils";
import NotificationListRow from "./notifications/NotificationListRow";
import NotificationPeriodPicker from "./notifications/NotificationPeriodPicker";
import { getNotificationPeriodHours } from "./notifications/notificationPeriodUtils";
import {
  filterAllowedNotifications,
  filterNotificationsByRead,
  groupNotificationsByDay,
  sortNotificationsByDateDesc,
} from "./notifications/notificationListUtils";

const AppNotifications = ({ setCookies, saveUser }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useMemo(
    () => JSON.parse(localStorage.getItem("user")) || null,
    [],
  );
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [periodHours, setPeriodHours] = useState(() =>
    getNotificationPeriodHours(),
  );

  const notifications = useSelector((state) => state.notification.data);
  const notificationStatus = useSelector((state) => state.notification.status);
  const isFetching = notificationStatus === "loading";

  useEffect(() => {
    dispatch(fetchNotification(periodHours));
    // Initial load only; period changes are handled in handlePeriodChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const allowedNotifications = useMemo(
    () => filterAllowedNotifications(notifications, user),
    [notifications, user],
  );

  const displayNotifications = useMemo(() => {
    const filtered = filterNotificationsByRead(
      allowedNotifications,
      showUnreadOnly,
    );
    return sortNotificationsByDateDesc(filtered);
  }, [allowedNotifications, showUnreadOnly]);

  const groupedNotifications = useMemo(
    () => groupNotificationsByDay(displayNotifications, t),
    [displayNotifications, t],
  );

  const unreadCount = useMemo(
    () => countUnreadAllowedNotifications(notifications, user),
    [notifications, user],
  );

  const hasFailed = notificationStatus === "failed";
  const isEmpty = !isFetching && displayNotifications.length === 0;
  const showReadFilters =
    !isFetching && !hasFailed && allowedNotifications.length > 0;

  const handlePeriodChange = (hours) => {
    if (hours === periodHours) {
      return;
    }
    setPeriodHours(hours);
    dispatch(fetchNotification(hours));
  };

  return (
    <div className="installation-list-page notifications-list-page">
      <AppHeaderBar
        title={t("Notifications")}
        setCookies={setCookies}
        saveUser={saveUser}
      />
      <main className="installation-list-shell">
        <div className="notifications-list-toolbar">
          <div className="notifications-list-toolbar-row">
            <NotificationPeriodPicker
              value={periodHours}
              onChange={handlePeriodChange}
              disabled={isFetching}
            />
            {showReadFilters && (
              <div className="notifications-list-toolbar-end">
                <div className="notifications-list-filter-group">
                  <button
                    type="button"
                    className={
                      !showUnreadOnly
                        ? "installation-list-toggle installation-list-toggle-active"
                        : "installation-list-toggle"
                    }
                    onClick={() => setShowUnreadOnly(false)}
                  >
                    {t("mobile_notifications_filter_all")}
                    <span className="notifications-list-filter-count">
                      (
                      {allowedNotifications.length > 99
                        ? "99+"
                        : allowedNotifications.length}
                      )
                    </span>
                  </button>
                  <button
                    type="button"
                    className={
                      showUnreadOnly
                        ? "installation-list-toggle installation-list-toggle-active"
                        : "installation-list-toggle"
                    }
                    onClick={() => setShowUnreadOnly(true)}
                    aria-label={
                      unreadCount > 0
                        ? t("mobile_notifications_filter_unread_with_count", {
                            count: unreadCount,
                          })
                        : t("mobile_notifications_filter_unread")
                    }
                  >
                    {t("mobile_notifications_filter_unread")}
                    <span className="notifications-list-filter-count">
                      ({unreadCount > 99 ? "99+" : unreadCount})
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {isFetching && (
          <div className="installation-detail-state notifications-list-refresh-state">
            <div className="installation-detail-spinner" aria-hidden />
            <p>{t("installation_loading")}</p>
          </div>
        )}

        {hasFailed && !isFetching && (
          <div className="installation-subpage-card notifications-list-error-card">
            <p className="installation-subpage-card-text">
              {t("mobile_notifications_load_error")}
            </p>
            <button
              type="button"
              className="installation-save-sheet-button"
              onClick={() => dispatch(fetchNotification(periodHours))}
            >
              {t("mobile_notifications_retry")}
            </button>
          </div>
        )}

        {!isFetching && !hasFailed && isEmpty && (
          <div className="installation-subpage-card monitoring-empty-card">
            <p className="installation-subpage-card-text">
              {showUnreadOnly
                ? t("mobile_notifications_empty_unread")
                : t("mobile_notifications_empty_period", {
                    hours: periodHours,
                  })}
            </p>
          </div>
        )}

        {!isFetching && !hasFailed && groupedNotifications.length > 0 && (
          <div className="notifications-list-groups">
            {groupedNotifications.map((group) => (
              <section key={group.label} className="notifications-list-group">
                <h2 className="notifications-list-group-heading">
                  {group.label}
                </h2>
                <div className="notifications-list-group-cards">
                  {group.items.map((notification) => (
                    <NotificationListRow
                      key={notification._id}
                      notification={notification}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
      <AppNavBar />
    </div>
  );
};

export default AppNotifications;
